import { useClerk, useSession } from "@clerk/nextjs";
import { useEffect, useRef } from "react";

import { type ClerkAuthRecoverySessionState, isClerkAuthRecoverySessionEligible } from "~/hooks/clerk-auth-recovery-session-eligibility";
import { type ClerkAuthRecoverySession, registerClerkAuthRecoverySession } from "~/services/clerk-auth-recovery-session.registry";

type ClerkSession = ReturnType<typeof useSession>["session"];

export function useClerkAuthRecoverySessionRegistration(isAppDomain: boolean): void {
  const clerk = useClerk();
  const { isLoaded: isSessionLoaded, isSignedIn, session } = useSession();
  const stateRef = useRef<{
    eligibility: ClerkAuthRecoverySessionState;
    session: ClerkSession;
  }>({
    eligibility: {
      isSessionLoaded,
      isSignedIn,
      sessionStatus: session?.status,
      organizationId: clerk.organization?.id ?? null,
    },
    session,
  });

  stateRef.current = {
    eligibility: {
      isSessionLoaded,
      isSignedIn,
      sessionStatus: session?.status,
      organizationId: clerk.organization?.id ?? null,
    },
    session,
  };

  useEffect(() => {
    if (!isAppDomain || typeof window === "undefined") {
      return;
    }

    const accessor: ClerkAuthRecoverySession = {
      isEligible: () => isClerkAuthRecoverySessionEligible(stateRef.current.eligibility),
      clearCache: () => {
        stateRef.current.session?.clearCache();
      },
      getToken: async () => {
        const activeSession = stateRef.current.session;
        if (activeSession == null) {
          return null;
        }
        const token = await activeSession.getToken({ skipCache: true });
        return token ?? null;
      },
    };

    return registerClerkAuthRecoverySession(accessor);
  }, [isAppDomain, isSessionLoaded, isSignedIn, session, clerk.organization?.id]);
}
